import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@/components/ui/Modal';
import { Clinic } from '../../types/models';
import { clinicsService } from '../../services/api/clinics.service';
import { useContext } from '@/hooks';
import toast from 'react-hot-toast';

interface DeleteClinicModalProps {
  clinic: Clinic;
  isOpen: boolean;
  onClose: () => void;
  onClinicDeleted: () => void;
}

export const DeleteClinicModal: React.FC<DeleteClinicModalProps> = ({
  clinic,
  isOpen,
  onClose,
  onClinicDeleted,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const { clinic: currentClinic, clearContext } = useContext();

  if (!isOpen) return null;

  const handleDelete = async () => {
    setIsDeleting(true);

    try {
      // Check if we're deleting the currently selected clinic
      const isDeletingCurrentClinic = currentClinic?.id === clinic.id;

      await clinicsService.delete(clinic.id);
      toast.success('Clínica excluída com sucesso!');

      // If deleting the current clinic, clear context and redirect
      if (isDeletingCurrentClinic) {
        clearContext();
        navigate('/clinicas');
      }

      onClinicDeleted();
      onClose();
    } catch (error: any) {
      console.error('Error deleting clinic:', error);

      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Erro ao excluir clínica');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="w-full max-w-md">
      <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <h2 className="text-xl font-semibold">Confirmar Exclusão</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isDeleting}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <p className="text-gray-600 mb-2">
            Tem certeza que deseja excluir a clínica <strong className="text-gray-900">{clinic.name}</strong>?
          </p>
          <p className="text-sm text-red-600 mt-4 p-3 bg-red-50 rounded-lg">
            <strong>Atenção:</strong> Esta ação não pode ser desfeita. Todos os dados relacionados a esta clínica serão permanentemente removidos.
          </p>
        </div>

        <div className="flex gap-3 p-6 border-t">
          <button
            type="button"
            onClick={handleClose}
            disabled={isDeleting}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? 'Excluindo...' : 'Excluir Clínica'}
          </button>
        </div>
    </Modal>
  );
};